import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { v4 as uuid } from 'uuid';

import { postProfitBoostApi } from '@flipster/apis/v2/trade';
import { FormattedMessage } from '@flipster/i18n';
import { accountBunja } from '@flipster/r230522/original/account';
import { Position } from '@flipster/r230522/original/position';
import { Badge } from '@flipster/react/design-system/badge';
import { Button } from '@flipster/react/design-system/button';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { TextButton } from '@flipster/react/design-system/text-button';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import { NoSSR } from '@flipster/react/misc/lifecycle/NoSSR';
import { css } from '@flipster/styled-system/css';
import { flex, hstack } from '@flipster/styled-system/patterns';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';
import Num, { numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { ProfitBoostEvent } from '@flipster/utils/segment/events/profit-boost';

import { MovableModalLegacy } from '../../movable-modal';
import { orderAttributionAtom } from '../../order-attribution';
import { getFlipsterUrl } from '../../urls';
import { useDepositModal } from '../../wallet-modal/deposit';
import { useProfitBoost } from './use-profit-boost';

interface Props {
  symbol: string;
  positionId: Position['positionId'];
  location: 'assets_tab' | 'order_form_page'; // only for logging
  onClose: () => void;
}

export const ConfirmProfitBoostStep = ({ onClose, symbol, positionId, location }: Props) => {
  const transPositionProfitBoost = useTranslations('Position_profit_boost.r250401');
  const transCommon = useTranslations('Common.r250401');
  const transTrade = useTranslations('Trade.r250401');
  const transAssets = useTranslations('Assets.r250401');
  const [isChecked, setIsChecked] = useState(false);
  const { cost, discountRate, percent, profitBoost, position } = useProfitBoost(symbol, positionId);

  const { v2AccountAtom } = useBunja(accountBunja);
  const account = useAtomValue(v2AccountAtom);
  const isOneWayMode = account?.tradeMode === 'OneWay';

  const openDepositModal = useDepositModal();
  const attribution = useAtomValue(orderAttributionAtom);

  const discountAmount = profitBoost?.costDiscounted
    ? Num(profitBoost.cost).minus(profitBoost.costDiscounted).toNumber()
    : 0;

  // v2 에러메시지 포맷의 경우 다국어 서버에러 대응이 안된 관계로, 직접 키 매핑하여 사용 (추후 대응되면 변경 요망)
  const errorMessageMap = {
    NotEnoughBalance: transPositionProfitBoost('Boost_failed_insufficient_balance_toast'),
    InvalidBoostDuration: transPositionProfitBoost('Invalid_boost_duration_error'),
    InvalidBoostCost: transPositionProfitBoost('Invalid_boost_cost_error'),
    BoostAlreadyExists: transPositionProfitBoost('Boost_already_exists_error'),
    BoostBuyLimitExceeded: transPositionProfitBoost('Boost_buy_limit_exceeded_error'),
    UnknownPosition: transPositionProfitBoost('Unknown_position_error'),
    BoostAmountTooLarge: transPositionProfitBoost('Boost_amount_too_large_error'),
    BoostAmountExceededIndividualLimit: transPositionProfitBoost('Boost_amount_exceeded_individual_limit_error'),
    BoostBlocked: transPositionProfitBoost('Boost_blocked_error'),
    TemporarilyUnavailableBoost: transPositionProfitBoost('Boost_unavailable_toast'),
  } as const;

  const handleConfirm = async () => {
    if (!position || !cost || !profitBoost?.durationInMin) return;
    segment.track(ProfitBoostEvent.ProfitBoostConfirmClick, {
      event_location: location,
      position_id: position.positionId,
      boost_cost: Number(cost),
      discount_rate: discountRate,
      duration: profitBoost.durationInMin,
      remaining_boost: position.boostDailyRemainingCount,
    });
    try {
      await postProfitBoostApi(symbol, isOneWayMode ? 0 : position.slot, {
        requestId: uuid(),
        cost,
        durationInMin: profitBoost.durationInMin,
        attribution,
      });

      snackbar.success({ message: transPositionProfitBoost('Boost_active_toast') });
    } catch (error) {
      const errorCode = getErrorCode(error);
      if (errorCode === 'NotEnoughBalance') {
        snackbar.error({
          message: errorMessageMap['NotEnoughBalance'],
          button: (
            <TextButton
              size='medium'
              color='accent'
              label={transAssets('Deposit_now')}
              onClick={() => {
                openDepositModal({ defaultValues: { currency: UsdtCurrency } });
                onClose();
              }}
            />
          ),
        });
      } else if (errorCode in errorMessageMap) {
        snackbar.error({ message: errorMessageMap[errorCode as keyof typeof errorMessageMap] });
      } else {
        snackbar.error({ message: getErrorMessage(error) });
      }
    } finally {
      onClose();
    }
  };

  return (
    <>
      <MovableModalLegacy.Header>
        <MovableModalLegacy.Title label={transPositionProfitBoost('Confirm_title')} />
      </MovableModalLegacy.Header>
      <MovableModalLegacy.Content
        css={{ p: { base: '24px 32px', tabletDown: '20px 16px' }, pb: { base: 24, tabletDown: 12 } }}
      >
        <div className={flex({ flexDir: 'column', gap: 24, height: '100%' })}>
          <div className={css({ flex: 1 })}>
            <dl
              className={css({
                display: 'flex',
                flexDirection: 'column',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.default.level-2',
                borderRadius: 8,
                gap: { base: 16, tabletDown: 8 },
                p: 16,

                '& > div': {
                  display: 'flex',
                  justifyContent: 'space-between',

                  '& dt': {
                    color: 'content.default.level-3',
                    textStyle: 'Text/S/Strong',
                    textAlign: 'left',
                  },

                  '& dd': {
                    color: 'content.default.level-5',
                    textStyle: 'Text/M/Strong',
                    textAlign: 'right',
                  },
                },
              })}
            >
              <div>
                <dt>{transPositionProfitBoost('Price_label')}</dt>
                <dd>
                  <div className={hstack({ gap: 4 })}>
                    <span>{numberFormatter(profitBoost?.cost, { precision: UsdtPrecision, toFixed: true })} USDT</span>
                  </div>
                </dd>
              </div>
              <div>
                <dt>{transPositionProfitBoost('Discount_amount_label')}</dt>
                <dd className={flex({ gap: 2, flexDirection: 'column' })}>
                  <div
                    className={css({
                      color: 'content.accent.promotion',
                    })}
                  >
                    <FormattedMessage
                      id='Autogen_common.r251126.s_value_numberFormatter_USDT'
                      values={{
                        value: discountAmount > 0 ? '-' : '',

                        numberFormatter: numberFormatter(discountAmount, {
                          precision: UsdtPrecision,
                          toFixed: true,
                        }),
                      }}
                    />
                  </div>
                  {percent && (
                    <div>
                      <Badge type='outline' color='promotion'>
                        {transPositionProfitBoost('Percent_discount_applied', { percent: `${percent}%` })}
                      </Badge>
                    </div>
                  )}
                </dd>
              </div>
              <div
                role='separator'
                className={css({
                  borderTopWidth: '1px',
                  borderTopStyle: 'solid',
                  borderColor: 'border.default.level-2',
                  my: { base: 0, tabletDown: 8 },
                })}
              />
              <div>
                <dt>{transPositionProfitBoost('Final_payment_amount_label')}</dt>
                <dd>
                  <span className={css({ textStyle: 'Title/XS' })}>
                    <FormattedMessage
                      id='Autogen_common.r251126.s_numberFormatter_USDT'
                      values={{
                        numberFormatter: numberFormatter(cost, {
                          precision: UsdtPrecision,
                          toFixed: true,
                        }),
                      }}
                    />
                  </span>
                </dd>
              </div>
            </dl>
          </div>
          <div>
            <Checkbox
              checked={isChecked}
              onChange={() => setIsChecked(!isChecked)}
              css={css.raw({
                '& input + div': {
                  marginTop: 4,
                },
              })}
            >
              {/* fixme.hoyong.i18n: 복합 문자열 처리케이스라서 추후 다국어 대응 */}
              <div
                className={css({
                  textStyle: 'Text/M/Regular',
                  color: 'content.default.level-5',
                  '& > a': { textStyle: 'Text/M/Regular!', padding: '0!', textDecoration: 'underline' },
                })}
              >
                I have read and agree to the{' '}
                <NoSSR>
                  <IsomorphicLink
                    href={`${getFlipsterUrl()}/policies/derivatives`}
                    className={css({ color: 'content.accent.info' })}
                    newTab
                    external
                  >
                    <FormattedMessage id='Autogen_common.r251126.s_Derivatives_Trading_Terms' />
                  </IsomorphicLink>
                </NoSSR>
                {' and '}
                <NoSSR>
                  <IsomorphicLink
                    href={`${getFlipsterUrl()}/policies/profit-boost`}
                    className={css({ color: 'content.accent.info' })}
                    newTab
                    external
                  >
                    <FormattedMessage id='Autogen_common.r251126.s_Profit_boost_Terms' />
                  </IsomorphicLink>
                </NoSSR>
              </div>
            </Checkbox>
          </div>
        </div>
      </MovableModalLegacy.Content>
      <MovableModalLegacy.Footer>
        <Button
          label={transCommon('Cancel')}
          color='neutral'
          hierarchy='secondary'
          size='large'
          onClick={() => {
            onClose();
          }}
        />
        <Button
          label={transTrade('Pay_now')}
          color='accent'
          size='large'
          disabled={!isChecked}
          withSpinner
          onClick={handleConfirm}
        />
      </MovableModalLegacy.Footer>
    </>
  );
};
